/** Normalized application error surfaced to the UI. */
export class AppError extends Error {
  code: string;
  status: number;
  fieldErrors?: Record<string, string>;

  constructor(message: string, code: string, status: number, fieldErrors?: Record<string, string>) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.status = status;
    this.fieldErrors = fieldErrors;
  }
}

interface BackendError {
  detail?: string | { msg?: string; loc?: (string | number)[] }[];
  code?: string;
  errors?: { loc: (string | number)[]; msg: string }[];
}

/** Map the backend envelope `{detail, code}` (and FastAPI validation errors) to an AppError. */
export function toAppError(status: number, body: unknown): AppError {
  const b = (body ?? {}) as BackendError;

  // FastAPI request-validation errors → per-field map.
  if (Array.isArray(b.errors) && b.errors.length > 0) {
    const fieldErrors: Record<string, string> = {};
    for (const e of b.errors) {
      const field = e.loc.filter((p) => p !== 'body').join('.');
      if (field) fieldErrors[field] = e.msg;
    }
    return new AppError('Please correct the highlighted fields.', 'VALIDATION_ERROR', status, fieldErrors);
  }

  const detail = typeof b.detail === 'string' ? b.detail : friendlyStatus(status);
  return new AppError(detail, b.code ?? `HTTP_${status}`, status);
}

function friendlyStatus(status: number): string {
  switch (status) {
    case 401:
      return 'Please sign in to continue.';
    case 403:
      return "You don't have permission to do that.";
    case 404:
      return 'We could not find what you were looking for.';
    case 429:
      return 'Too many requests. Please slow down and try again shortly.';
    default:
      return status >= 500 ? 'Something went wrong on our end.' : 'Request failed.';
  }
}
